/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React from 'react';
import { getPartialDays } from '../Utils/Graph/AllGraphUtils';
import '../../../scss/TooltipCommonTheme.scss';
import { UtilityType } from '../Utils/Constants';
import './TrendChart.scss';
import { Chart } from '@scuf/charts';
import { TFunction } from '@scuf/localization/dist/types';
import descriptors from '../../../locales/en-US/descriptors';
import {
  actualAvoidanceCondition,
  baselineCOnsumptionCondition,
  previousAvoidanceCondition
} from '../Utils/Utils';

export const actualConsumptionTooltipPaneItem = (
  actualConsumptionData: (number | null)[] | undefined,
  category: string | undefined,
  t: TFunction,
  isCompletedData: boolean,
  partialDate: number,
  actualValue: number | null | undefined,
  measurementUnit: string,
  renderedFromCarbon?: boolean
) => {
  const d = descriptors.energyManagement;
  if (actualConsumptionData && category !== UtilityType.All) {
    return (
      <div className="tooltip-pane-item">
        <div className="tooltip-heading-container">
          <span className="dot" style={{ backgroundColor: '#1272B2' }}></span>
          {category === UtilityType.CO2 ? (
            <span className="tooltip-header-label emaasThemeColor">
              {t(d.ACTUAL)} {t(d.EMISSION)}
              {getPartialDays(isCompletedData, partialDate, t)}
            </span>
          ) : (
            <span className="tooltip-header-label emaasThemeColor">
              {renderedFromCarbon ? t(d.ACTUALEMISSIONS) : t(d.ACTUAL)}
              {getPartialDays(isCompletedData, partialDate, t)}
            </span>
          )}
        </div>
        <div>
          <span className="tooltip-header-value emaasThemeColor">
            {actualValue} {measurementUnit}
          </span>
        </div>
      </div>
    );
  } else {
    return undefined;
  }
};

export const baselineConsumptionTooltipPaneItem = (
  baselineConsumptionData: (number | null)[] | undefined,
  category: string | undefined,
  t: TFunction,
  baselineValue: number | null | undefined,
  measurementUnit: string,
  renderedFromCarbon?: boolean
) => {
  if (baselineConsumptionData && category !== UtilityType.All) {
    return (
      <div className="tooltip-pane-item">
        <div className="tooltip-heading-container">
          <span className="dot" style={{ backgroundColor: '#606061' }}></span>
          <span className="tooltip-header-label emaasThemeColor">
            {baselineCOnsumptionCondition(category, t, renderedFromCarbon)}
          </span>
        </div>
        <div>
          <span className="tooltip-header-value emaasThemeColor">
            {baselineValue} {measurementUnit}
          </span>
        </div>
      </div>
    );
  } else {
    return undefined;
  }
};

export const actualAvoidanceTooltipPaneItem = (
  actualAvoidanceData: (number | null)[] | undefined,
  category: string | undefined,
  t: TFunction,
  actualValue: number | null | undefined,
  measurementUnit: string,
  renderedFromCarbon?: boolean
) => {
  if (actualAvoidanceData && category !== UtilityType.All) {
    return (
      <div className="tooltip-pane-item">
        <div className="tooltip-heading-container">
          <span className="dot" style={{ backgroundColor: '#7DB52C' }}></span>
          <span className="tooltip-header-label emaasThemeColor">
            {actualAvoidanceCondition(category, t, renderedFromCarbon)}
          </span>
        </div>
        <div>
          <span className="tooltip-header-value emaasThemeColor">
            {actualValue} {measurementUnit}
          </span>
        </div>
      </div>
    );
  } else {
    return undefined;
  }
};

export const previousAvoidanceTooltipPaneItem = (
  previousAvoidanceData: (number | null)[] | undefined,
  category: string | undefined,
  t: TFunction,
  previousValue: number | null | undefined,
  measurementUnit: string,
  renderedFromCarbon?: boolean
) => {
  if (previousAvoidanceData && category !== UtilityType.All) {
    return (
      <div className="tooltip-pane-item">
        <div className="tooltip-heading-container">
          <span className="dot" style={{ backgroundColor: '#4E662B' }}></span>
          <span className="tooltip-header-label emaasThemeColor">
            {previousAvoidanceCondition(category, t, renderedFromCarbon)}
          </span>
        </div>
        <div>
          <span className="tooltip-header-value emaasThemeColor">
            {previousValue} {measurementUnit}
          </span>
        </div>
      </div>
    );
  } else {
    return undefined;
  }
};

export const baselineCostDisplay = (
  currencySymbol: string,
  baselineCostData: number[] | undefined,
  baselineConsumptionData: (number | null)[] | undefined,
  actualConsumptionData: (number | null)[] | undefined,
  t: TFunction
) => {
  const d = descriptors.energyManagement;
  if (baselineCostData) {
    return (
      <Chart.Line
        name={`${currencySymbol} ${d.BASELINECOST.defaultMessage}`}
        data={baselineCostData}
        index={undefined}
        dashStyle="ShortDash"
        id={'2'}
        yAxis={baselineConsumptionData || actualConsumptionData ? 1 : 0}
      />
    );
  } else {
    return undefined;
  }
};
